/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2021 - 2021
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useState } from "react";

import PropTypes from "prop-types";
import _filter from "lodash/filter";
import styled from "styled-components";

import blueGrey from "@material-ui/core/colors/blueGrey";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Checkbox, Chip } from "@material-ui/core";
import StyledTextField from "./StyledTextField";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const StyledChip = styled(Chip)`
  color: #fff !important;
  background-color: ${blueGrey[700]} !important;
  padding: 0px 4px 0px 0px !important;
  width: 100%;
  justify-content: space-between !important;
  .MuiChip-deleteIcon {
    background-color: white;
    color: ${blueGrey[700]};
    height: 15px;
    width: 15px;
    border-radius: 100%;
  }
`;

const StyledAutocomplete = styled(Autocomplete)`
  .MuiInputLabel-root {
    z-index: 1100 !important;
  }

  .Mui-focused {
    background-color: #e0e0e0; // Match the navbar color
    margin-top: -9px; // Need to push up to account for grid padding
    position: absolute; // Need absolute to allow to overflow navbar
    z-index: 1000;
    top:0;
    left: 0;
    right: 0;
  }
`;

const AutoCompleteMultiSelectCheckboxList = ({
  label,
  disabled,
  options,
  selectedOptions,
  setSelectedOptions,
  autoCompleteTestId,
  width,
  onClose,
  cutDownLabels
}) => {

  const [isFocused, setIsFocused] = useState(false);

  const handleNewSelection = (newList, option) => {
    if (newList.length == 0) {
      setSelectedOptions([]);
    }
    else {
      setSelectedOptions(newList);
    }
  };

  const showOptionLabel = (more) => {

    // Show the selection if we've only selected one
    if ({ cutDownLabels }) {
      if (selectedOptions && selectedOptions.length == 1) {
        if (selectedOptions[0].title.length <= 8) {
          return selectedOptions[0].title;
        }
        else {
          return selectedOptions[0].title.substring(0, 8) + "...";
        }
      }
    }
    else {
      if (selectedOptions && selectedOptions.length == 1) {
        return selectedOptions[0].title;
      }
    }

    // Otherwise show how many we have selected
    return `(${more}) Selected`;
  }

  const getLabel = () => {
    let fullLabel = "Any " + label;

    if (isFocused || (selectedOptions && selectedOptions.length > 0)) {
      return label;
    }
    return fullLabel;
  };

  return (
    <>
      <StyledAutocomplete
        multiple
        style={
          width ? { width } : {}
        }
        disabled={disabled}
        data-testid={autoCompleteTestId}
        id={autoCompleteTestId}
        disableCloseOnSelect
        disableClearable
        limitTags={0}
        onClose={onClose}
        value={selectedOptions}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        getOptionSelected={(option, value) => option.id === value.id}
        renderTags={(value, getTagProps) => {
          return (
            value.sort(function (a, b) {
              let keyA = a.id;
              let keyB = b.id;
              // Compare the state acronym
              if (keyA < keyB) return -1;
              if (keyA > keyB) return 1;
              return 0;
            }).map((option, index) => (
              <StyledChip
                {...getTagProps({ index })}
                key={option.id}
                label={option.title}
              />
            ))
          )
        }}
        getLimitTagsText={(more) => showOptionLabel(more)}
        options={options ? options : []}
        getOptionLabel={(option) => `${option.title}`}
        onChange={(e, newList, action, option) => {
          if (e.key !== "Backspace") {
            handleNewSelection(newList, option);
          }
        }}
        renderOption={(option, { selected }) => (
          <>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
              key={option.id}
            />
            {option.title}
          </>
        )}
        renderInput={(params) => (
          <StyledTextField
            {...params}
            label={getLabel()}
            variant="outlined"
          />
        )}
      />
    </>
  );
};

AutoCompleteMultiSelectCheckboxList.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  selectedOptions: PropTypes.array.isRequired,
  setSelectedOptions: PropTypes.func.isRequired,
  autoCompleteTestId: PropTypes.string,
  width: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  cutDownLabels: PropTypes.bool.isRequired
};
export default AutoCompleteMultiSelectCheckboxList;
